<script setup lang="ts">
import { getExactOnlineAuthorizeUrl, useGetExactOnlineConnectionStatus, useSearchExactOnlineCustomerCompanies } from '@swipe4work/api-orval'
import type { SearchCustomerCompaniesWithExactDataFromAdminCustomerCompanyWithExactDataDto } from '@swipe4work/models-orval'
import { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'
import UpdateExactLinkFormDialog from '@/features/settings/dialogs/UpdateExactLinkFormDialog.vue'
import type { ExactCompanyOption } from '@/shared/components/form/ExactCompanySelector.vue'
import { usePermissions } from '@/shared/composables/UsePermissions'
import { useRouter } from 'vue-router'

definePage({
  path: '/settings/exact-online',
  name: '/settings/ExactOnline',
  meta: {
    title: 'Exact Online',
    permission: {
      resource: 'ExactOnline',
      level: 'Read',
    },
  },
})

const { can } = usePermissions()

// ── Connection status ─────────────────────────────────────────────────────
const { data, isPending, refetch } = useGetExactOnlineConnectionStatus()
const isConnected = computed(() => data.value?.isConnected ?? false)

const isOpening = ref(false)

async function openAuthorizeWindow() {
  isOpening.value = true
  try {
    const { url } = await getExactOnlineAuthorizeUrl()
    const popup = window.open(url, 'exactonline-auth', 'width=800,height=700')
    const poll = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(poll)
        refetch()
      }
    }, 500)
  }
  finally {
    isOpening.value = false
  }
}

// ── Customer companies overview ──────────────────────────────────────────
type BoolFilter = boolean | null

type FilterState = {
  hasExactLink: BoolFilter
  hasInvoice: BoolFilter
}

const {
  filters,
  draft,
  searchInput,
  search,
  sortField,
  sortDescending,
  offset,
  rows,
  ROWS_PER_PAGE_OPTIONS,
  activeFilterCount,
  onFilterPanelShow,
  onFilterApply,
  onFilterClear,
  onSort,
  onPage,
} = useDataTableFilters<FilterState>({
  schema: {
    hasExactLink: 'boolean',
    hasInvoice: 'boolean', 
  },
  defaultFilters: () => ({
    hasExactLink: null,
    hasInvoice: null, 
  }),
  persistToStorage: true,
  defaultSortField: 'name',
})

const linkFilterOptions = [
  {
    label: 'Alle bedrijven',
    value: null as BoolFilter, 
  },
  {
    label: 'Gekoppeld aan Exact',
    value: true as BoolFilter, 
  },
  {
    label: 'Niet gekoppeld',
    value: false as BoolFilter, 
  },
]

const invoiceFilterOptions = [
  {
    label: 'Alle bedrijven',
    value: null as BoolFilter, 
  },
  {
    label: 'Heeft factuur',
    value: true as BoolFilter, 
  },
  {
    label: 'Geen factuur',
    value: false as BoolFilter, 
  },
]

const params = computed(() => ({
  search: search.value || undefined,
  hasExactLink: filters.hasExactLink ?? undefined,
  hasInvoice: filters.hasInvoice ?? undefined,
  sortField: sortField.value,
  sortDescending: sortDescending.value ? true : undefined,
  Limit: rows.value,
  Offset: offset.value,
}))

const {
  data: companiesData,
  isPending: isCompaniesPending,
  isError: isCompaniesError,
} = useSearchExactOnlineCustomerCompanies(params, {
  query: { staleTime: 30_000 },
})

const results = computed(() => companiesData.value?.companies ?? [])
const totalCount = computed(() => companiesData.value?.totalCount ?? 0)

const linkedCount = computed(() => results.value.filter(c => c.exactCompany).length)

// ── Edit dialog wiring ────────────────────────────────────────────────────
const dialog = useDynamicDialog()
const router = useRouter()

function openUpdateDialog(company: SearchCustomerCompaniesWithExactDataFromAdminCustomerCompanyWithExactDataDto) {
  const currentLink: ExactCompanyOption | null = company.exactCompany
    ? {
      exactGuid: company.exactCompany.exactGuid,
      exactId: company.exactCompany.exactId,
      name: company.exactCompany.name,
    }
    : null

  dialog.open(UpdateExactLinkFormDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
    data: {
      customerCompanyId: company.id,
      companyName: company.name,
      currentLink,
    },
  })
}

// ── Context menu wiring ──────────────────────────────────────────────────
const menu = ref()
const selectedCompany = ref<SearchCustomerCompaniesWithExactDataFromAdminCustomerCompanyWithExactDataDto>()

const contextMenuItems = computed(() => [
  {
    label: 'Bekijk bedrijf',
    icon: 'fa-regular fa-info-circle',
    command: () => {
      if (!selectedCompany.value) return
      router.push({
        name: 'companies.[id]',
        params: { id: selectedCompany.value.id },
      })
    },
  },
  {
    label: 'Wijzig koppeling',
    icon: can(AdminPermissionResource.ExactOnline, PermissionLevel.Write) ? 'fa-regular fa-edit' : 'fa-regular fa-lock',
    disabled: !can(AdminPermissionResource.ExactOnline, PermissionLevel.Write),
    command: () => {
      if (!selectedCompany.value) return
      openUpdateDialog(selectedCompany.value)
    },
  },
])

function onRowContextMenu(event: { originalEvent: Event }) {
  menu.value?.show(event.originalEvent)
}

function openContextMenu(
  event: { originalEvent: Event },
  company: SearchCustomerCompaniesWithExactDataFromAdminCustomerCompanyWithExactDataDto,
) {
  selectedCompany.value = company
  menu.value?.show(event.originalEvent)
}
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <Card>
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fa-regular fa-link text-primary-500" />
          <span>Exact Online</span>
        </div>
      </template>
      <template #content>
        <div class="flex items-center justify-between gap-4">
          <div class="flex flex-col gap-1">
            <span class="text-sm text-surface-600">Koppeling status</span>
            <Skeleton v-if="isPending" height="1.75rem" width="7rem" />
            <Tag
              v-else
              :severity="isConnected ? 'success' : 'warn'"
              :value="isConnected ? 'Gekoppeld' : 'Niet gekoppeld'"
            />
          </div>

          <Button
            v-permission="{ resource: AdminPermissionResource.ExactOnline, level: PermissionLevel.Write }"
            :label="isConnected ? 'Opnieuw koppelen' : 'Koppelen met Exact Online'"
            :icon="isConnected ? 'fa-regular fa-arrows-rotate' : 'fa-regular fa-link'"
            :severity="isConnected ? 'secondary' : 'primary'"
            :loading="isOpening"
            :disabled="isPending || isOpening"
            @click="openAuthorizeWindow"
          />
        </div>

        <Divider />

        <p class="m-0 text-sm text-surface-500">
          Klik op "Koppelen" om een nieuw browservenster te openen. Log in bij Exact Online en
          geef toestemming. De koppeling wordt automatisch opgeslagen zodra u akkoord gaat.
        </p>
      </template>
    </Card>

    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between gap-4">
            <div class="flex flex-col gap-1">
              <h3 class="text-lg font-semibold">
                Klantbedrijven & Exact koppelingen
              </h3>
              <span class="text-xs text-surface-500">
                {{ totalCount }} bedrijven — {{ linkedCount }} van de huidige pagina gekoppeld
              </span>
            </div>
            <div class="flex items-center gap-3">
              <DataTableFilterPanel
                :active-filter-count="activeFilterCount"
                @show="onFilterPanelShow"
                @apply="onFilterApply"
                @clear="onFilterClear"
              >
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col gap-2">
                    <label class="text-sm text-surface-700 font-semibold">
                      Koppelingsstatus
                    </label>
                    <div
                      v-for="option in linkFilterOptions"
                      :key="String(option.value)"
                      class="flex items-center gap-2"
                    >
                      <RadioButton
                        v-model="draft.hasExactLink"
                        :input-id="`linkFilter-${option.value}`"
                        :value="option.value"
                      />
                      <label :for="`linkFilter-${option.value}`" class="text-sm">
                        {{ option.label }}
                      </label>
                    </div>
                  </div>

                  <div class="flex flex-col gap-2">
                    <label class="text-sm text-surface-700 font-semibold">
                      Facturen
                    </label>
                    <div
                      v-for="option in invoiceFilterOptions"
                      :key="String(option.value)"
                      class="flex items-center gap-2"
                    >
                      <RadioButton
                        v-model="draft.hasInvoice"
                        :input-id="`invoiceFilter-${option.value}`"
                        :value="option.value"
                      />
                      <label :for="`invoiceFilter-${option.value}`" class="text-sm">
                        {{ option.label }}
                      </label>
                    </div>
                  </div>
                </div>
              </DataTableFilterPanel>

              <IconField class="max-w-xs flex-1">
                <InputIcon class="fa-regular fa-search" />
                <InputText
                  v-model="searchInput"
                  placeholder="Zoek op bedrijf, code of Exact-naam..."
                  class="w-full"
                />
                <InputIcon
                  v-if="searchInput"
                  class="fa-regular fa-xmark cursor-pointer"
                  style="pointer-events: auto"
                  @click="searchInput = ''"
                />
              </IconField>
            </div>
          </div>

          <div v-if="isCompaniesPending" class="flex flex-col gap-2">
            <Skeleton
              v-for="n in 8"
              :key="n"
              height="3rem"
            />
          </div>
          <Message
            v-else-if="isCompaniesError"
            severity="error"
          >
            Er is iets misgegaan bij het laden van de bedrijven.
          </Message>
          <div v-else class="overflow-x-auto">
            <ContextMenu ref="menu" :model="contextMenuItems" @hide="selectedCompany = undefined" />
            <DataTable
              v-model:context-menu-selection="selectedCompany"
              :value="results"
              :lazy="true"
              :first="offset"
              :rows="rows"
              :rows-per-page-options="ROWS_PER_PAGE_OPTIONS"
              :total-records="totalCount"
              :sort-field="sortField"
              :sort-order="sortDescending ? -1 : 1"
              paginator
              paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
              current-page-report-template="{first}-{last} van {totalRecords}"
              size="small"
              class="w-full"
              row-hover
              context-menu
              @row-click="(e) => openContextMenu(e, e.data)"
              @row-contextmenu="onRowContextMenu"
              @page="onPage"
              @sort="onSort"
            >
              <Column
                field="name"
                header="Bedrijf"
                sortable
                style="min-width: 250px"
                header-class="text-xs"
              >
                <template #body="{ data: company }">
                  <div class="flex items-center gap-2">
                    <Avatar
                      v-if="company.logoUrl"
                      :image="company.logoUrl"
                      shape="circle"
                      :pt="{ image: { class: 'object-contain' } }"
                    />
                    <Avatar
                      v-else
                      shape="circle"
                      :label="company.name.charAt(0)"
                    />
                    <h3
                      v-tooltip.right="company.name"
                      class="line-clamp-1 font-semibold"
                    >
                      {{ company.name }}
                    </h3>
                  </div>
                </template>
              </Column>
              <Column
                field="exactId"
                header="Klant id"
                sortable
                style="min-width: 11rem"
                header-class="text-xs"
              >
                <template #body="{ data: company }">
                  <span v-if="company.exactCompany" class="text-sm font-mono">
                    {{ company.exactCompany.exactId }}
                  </span>
                  <span v-else class="text-sm text-surface-400">—</span>
                </template>
              </Column>
              <Column
                field="exactName"
                header="Exact bedrijfsnaam"
                sortable
                style="min-width: 14rem"
                header-class="text-xs"
              >
                <template #body="{ data: company }">
                  <span v-if="company.exactCompany" class="text-sm">
                    {{ company.exactCompany.name }}
                  </span>
                  <span v-else class="text-sm text-surface-400">—</span>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>
